import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Users, Bell, Flag, CheckCircle2, Loader2, Pencil, Share2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import RichDescription from "@/components/RichDescription";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CATEGORY_META, STATUS_META, STATUS_ORDER, UNABLE_REASONS } from "@/lib/communityMeta";

export default function IssueDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const allowed = user && ["farmer", "day_worker"].includes(user.role);
  const [item, setItem] = useState(null);
  const [flags, setFlags] = useState({ is_admin: false, is_rep: false });
  const [showReport, setShowReport] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [showUnable, setShowUnable] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [unableForm, setUnableForm] = useState({ reason: "government_required", actions_taken: "", support_required: "", department: "", note: "" });
  const [resolveForm, setResolveForm] = useState({ description: "", work_completed: "", remaining_limitations: "" });
  const [referralForm, setReferralForm] = useState({ department: "", reference_number: "", note: "" });
  const [newStatus, setNewStatus] = useState("under_review");
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "", village: "", landmark: "" });
  const [statusNote, setStatusNote] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    api.get(`/issues/${id}`).then((r) => setItem(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(() => {
    if (!allowed) return;
    refresh();
    api.get("/me/community-flags").then((r) => setFlags(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, allowed]);

  if (!allowed) {
    return <AppShell><div className="p-24 text-center">Not available in this workspace.</div></AppShell>;
  }
  if (!item) {
    return <AppShell><div className="p-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div></AppShell>;
  }

  const meta = CATEGORY_META[item.category] || CATEGORY_META.other;
  const sm = STATUS_META[item.status] || STATUS_META.reported;
  const isOwner = user && item && user.id === item.created_by;
  const canEdit = isOwner && ["reported", "under_review", "awaiting_assignment"].includes(item.status);
  const canDelete = isOwner || flags.is_admin;

  const openEdit = () => {
    setEditForm({ title: item.title, description: item.description,
                  village: item.village, landmark: item.landmark || "" });
    setShowEdit(true);
  };
  const saveEdit = async () => {
    if (!editForm.title.trim() || editForm.title.trim().length < 4) return toast.error("Title too short");
    try {
      // PUT /issues/{id} expects full IssueIn — merge our edit over the existing doc.
      const payload = { ...item, ...editForm, photos: item.photos || [], videos: item.videos || [] };
      // IssueIn only accepts a known field set
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      delete payload.affected_count; delete payload.status; delete payload.updates;
      delete payload.created_by; delete payload.created_by_name; delete payload.created_by_role;
      delete payload.assigned_rep_id; delete payload.assigned_rep_name;
      delete payload.joined; delete payload.following; delete payload.status_label;
      delete payload.responsibility_status; delete payload.resolution_proposal;
      delete payload.unable_feedback; delete payload.referrals;
      await api.put(`/issues/${id}`, payload);
      toast.success("Issue updated");
      setShowEdit(false);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteIssue = async () => {
    if (!window.confirm("Delete this issue? Audit history is preserved.")) return;
    try { await api.delete(`/issues/${id}`); toast.success("Issue removed"); nav("/community"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const canModerate = flags.is_admin || (flags.is_rep && item.assigned_rep_id === user.id);
  const isAssignedRep = flags.is_rep && item.assigned_rep_id === user.id;
  const needsAcceptance = isAssignedRep && !["assigned", "in_progress", "resolution_submitted", "unable_to_resolve", "resolved", "closed_not_resolved"].includes(item.status);
  const canSubmitResolution = isAssignedRep && ["assigned", "in_progress"].includes(item.status);
  const canReviewResolution = flags.is_admin && item.status === "resolution_submitted";

  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/issues/${id}/affected`);
      toast.success(data.already ? "You have already joined this issue" : "You have joined this issue");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const follow = async () => {
    try { await api.post(`/issues/${id}/follow`); toast.success("You will receive updates on this issue"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reportAbuse = async (reason) => {
    try { await api.post(`/issues/${id}/report`, { reason }); toast.success("Thank you — our team will review"); setShowReport(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitStatus = async () => {
    setBusy(true);
    try {
      await api.post(`/issues/${id}/status`, { status: newStatus, note: statusNote });
      toast.success("Status updated");
      setShowStatus(false); setStatusNote("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const acceptAssignment = async (accept) => {
    try { await api.post(`/issues/${id}/assignment-response`, { accept });
      toast.success(accept ? "Assignment accepted" : "Assignment declined"); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const submitResolution = async () => {
    if (!resolveForm.description.trim()) return toast.error("Please describe the resolution");
    try { await api.post(`/issues/${id}/resolve-submit`, resolveForm);
      toast.success("Resolution submitted — Admin will review"); setShowResolve(false); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const submitUnable = async () => {
    try { await api.post(`/issues/${id}/unable-to-resolve`, unableForm);
      toast.success("Submitted for Admin review"); setShowUnable(false); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const submitReferral = async () => {
    if (!referralForm.department.trim()) return toast.error("Department is required");
    try { await api.post(`/issues/${id}/referral`, referralForm);
      toast.success("Referral recorded"); setShowReferral(false); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reviewResolution = async (decision) => {
    try { await api.post(`/issues/${id}/review-resolution`, { decision });
      toast.success(`Resolution ${decision}d`); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-4xl mx-auto">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="mt-4 rounded-3xl bg-white border border-gray-100 p-6 md:p-8 premium-shadow">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="text-4xl">{meta.icon}</div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-gray-500">{meta.label}</div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">{item.title}</h1>
              </div>
            </div>
            <span className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${sm.color}`}>{sm.label}</span>
          </div>
          {(isOwner || flags.is_admin) && (
            <div className="mt-4 flex gap-2 flex-wrap">
              {canEdit && (
                <button data-testid="issue-edit" onClick={openEdit}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
              )}
              <button data-testid="issue-share" onClick={() => {
                const url = `${window.location.origin}/community/${id}`;
                if (navigator.share) {
                  navigator.share({ title: item.title, url }).catch(() => {});
                } else {
                  navigator.clipboard.writeText(url);
                  toast.success("Link copied");
                }
              }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                <Share2 className="h-3.5 w-3.5" /> Share
              </button>
              {canDelete && (
                <button data-testid="issue-delete" onClick={deleteIssue}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-rose-200 text-xs font-semibold text-rose-700 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              )}
            </div>
          )}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><div className="text-[10px] uppercase tracking-widest text-gray-500">Village</div><div className="font-semibold text-gray-900 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{item.village}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-gray-500">Reported</div><div className="font-semibold text-gray-900">{new Date(item.created_at).toLocaleDateString()}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-gray-500">Affected</div><div className="font-semibold text-gray-900 flex items-center gap-1"><Users className="h-3.5 w-3.5" />{item.affected_count || 1}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-gray-500">Representative</div><div className="font-semibold text-gray-900">{item.assigned_rep_name || "Unassigned"}</div></div>
          </div>
          {item.landmark && <div className="mt-3 text-sm text-gray-600">📍 Landmark: <b>{item.landmark}</b></div>}

          <div className="mt-6 border-t border-gray-100 pt-6">
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-2">About the issue</div>
            <RichDescription text={item.description} />
          </div>

          {Array.isArray(item.photos) && item.photos.length > 0 && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
              {item.photos.map((p, i) => (
                <img key={i} src={p} alt="" className="w-full aspect-video object-cover rounded-xl border border-gray-200" />
              ))}
            </div>
          )}

          {/* Status timeline */}
          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-3">Status Timeline</div>
            <div className="flex items-center flex-wrap gap-y-3">
              {STATUS_ORDER.map((st, idx) => {
                const reached = STATUS_ORDER.indexOf(item.status) >= idx;
                const now = STATUS_ORDER.indexOf(item.status) === idx;
                const m = STATUS_META[st];
                return (
                  <div key={st} className="flex items-center flex-1 min-w-[120px]">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${reached ? m.dot + " text-white" : "bg-gray-200 text-gray-500"}`}>
                      {reached ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-xs">{idx + 1}</span>}
                    </div>
                    <div className={`ml-2 text-[11px] uppercase tracking-wider ${now ? "font-bold text-gray-900" : reached ? "text-gray-700" : "text-gray-400"}`}>{m.label}</div>
                    {idx < STATUS_ORDER.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${reached ? m.dot : "bg-gray-200"}`} />}
                  </div>
                );
              })}
            </div>
          </div>

          {(item.updates || []).length > 0 && (
            <div className="mt-8">
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-3">Updates</div>
              <div className="space-y-3">
                {item.updates.map((u) => (
                  <div key={u.id} className="rounded-2xl bg-gray-50 border border-gray-100 p-4 text-sm">
                    <div className="font-semibold text-gray-900">{STATUS_META[u.status]?.label || u.status}</div>
                    {u.note && <div className="mt-1 text-gray-700 whitespace-pre-line">{u.note}</div>}
                    <div className="mt-1 text-xs text-gray-500">{u.by_user_name} · {new Date(u.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(item.referrals) && item.referrals.length > 0 && (
            <div className="mt-6 rounded-2xl bg-indigo-50 border border-indigo-200 p-4">
              <div className="text-xs uppercase tracking-widest text-indigo-900 font-semibold">External Referrals</div>
              <div className="mt-2 space-y-2 text-sm">
                {item.referrals.map((r, i) => (
                  <div key={i} className="rounded-xl bg-white border border-indigo-100 p-3">
                    <div className="font-semibold text-gray-900">{r.department}</div>
                    {r.reference_number && <div className="text-xs text-gray-600">Ref #: {r.reference_number}</div>}
                    {r.note && <div className="text-xs text-gray-600 mt-1 whitespace-pre-line">{r.note}</div>}
                    <div className="text-[11px] text-gray-500 mt-1">by {r.recorded_by_name} · {new Date(r.recorded_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[11px] text-indigo-900">
                Recorded as a follow-up with the responsible authority. GTF does not guarantee that the authority will resolve the issue.
              </div>
            </div>
          )}

          {item.unable_feedback && (
            <div className="mt-6 rounded-2xl bg-rose-50 border border-rose-200 p-4 text-sm">
              <div className="text-xs uppercase tracking-widest text-rose-900 font-semibold">Resolver feedback — Admin review required</div>
              <div className="mt-2 space-y-1">
                <div><b>Reason:</b> {(UNABLE_REASONS.find(([k]) => k === item.unable_feedback.reason) || ["", item.unable_feedback.reason])[1]}</div>
                {item.unable_feedback.actions_taken && <div><b>Actions already taken:</b> {item.unable_feedback.actions_taken}</div>}
                {item.unable_feedback.support_required && <div><b>Further support required:</b> {item.unable_feedback.support_required}</div>}
                {item.unable_feedback.department && <div><b>Relevant department:</b> {item.unable_feedback.department}</div>}
                {item.unable_feedback.note && <div className="whitespace-pre-line"><b>Note:</b> {item.unable_feedback.note}</div>}
                <div className="text-[11px] text-rose-900">Submitted by {item.unable_feedback.submitted_by_name} · {new Date(item.unable_feedback.submitted_at).toLocaleString()}</div>
              </div>
            </div>
          )}

          <div className="mt-6 text-[11px] text-gray-500 italic border-t border-gray-100 pt-4">
            Grow Together Farmily provides a platform to report, share and coordinate visibility of local community issues. Resolution depends on the concerned people, authorities or organizations.
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="issue-join" onClick={join} disabled={busy || item.joined}
              className={`px-5 py-2.5 rounded-full font-semibold ${item.joined ? "bg-gray-100 text-gray-500" : theme.bg + " text-white"}`}>
              {item.joined ? "✓ You are affected" : "I'm also affected"}
            </button>
            <button data-testid="issue-follow" onClick={follow} disabled={item.following}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold ${item.following ? "bg-gray-100 text-gray-500" : "bg-white border border-gray-200 text-gray-800"}`}>
              <Bell className="h-4 w-4" /> {item.following ? "Following" : "Follow issue"}
            </button>
            <button data-testid="issue-report" onClick={() => setShowReport(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-gray-200 text-gray-700">
              <Flag className="h-4 w-4" /> Report
            </button>
            {canModerate && (
              <button data-testid="issue-change-status" onClick={() => setShowStatus(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white font-semibold">
                Change status
              </button>
            )}
            {needsAcceptance && (
              <>
                <button data-testid="issue-accept" onClick={() => acceptAssignment(true)}
                  className="px-5 py-2.5 rounded-full bg-emerald-600 text-white font-semibold">Accept assignment</button>
                <button data-testid="issue-decline" onClick={() => acceptAssignment(false)}
                  className="px-5 py-2.5 rounded-full bg-white border border-gray-200 text-gray-800">Decline</button>
              </>
            )}
            {canSubmitResolution && (
              <>
                <button data-testid="issue-resolve-submit" onClick={() => setShowResolve(true)}
                  className="px-5 py-2.5 rounded-full bg-sky-600 text-white font-semibold">Submit Resolution</button>
                <button data-testid="issue-unable" onClick={() => setShowUnable(true)}
                  className="px-5 py-2.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200 font-semibold">Unable to Resolve</button>
              </>
            )}
            {(canModerate || flags.is_admin) && (
              <button data-testid="issue-referral" onClick={() => setShowReferral(true)}
                className="px-5 py-2.5 rounded-full bg-white border border-gray-200 text-gray-800 font-semibold">Record Referral</button>
            )}
            {canReviewResolution && (
              <>
                <button data-testid="issue-review-approve" onClick={() => reviewResolution("approve")}
                  className="px-5 py-2.5 rounded-full bg-emerald-600 text-white font-semibold">Approve Resolution</button>
                <button data-testid="issue-review-return" onClick={() => reviewResolution("return")}
                  className="px-5 py-2.5 rounded-full bg-white border border-gray-200 text-gray-800 font-semibold">Return for more work</button>
                <button data-testid="issue-review-close" onClick={() => reviewResolution("close_not_resolved")}
                  className="px-5 py-2.5 rounded-full bg-gray-100 text-gray-700 border border-gray-300 font-semibold">Close — Not Resolved</button>
              </>
            )}
          </div>
        </div>
      </div>

      {showReport && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowReport(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-gray-900">Report this issue</div>
            <div className="text-xs text-gray-500 mt-1">Help us keep GTF clean. Our team will review.</div>
            <div className="mt-4 space-y-2">
              {["fake", "duplicate", "spam", "abusive", "wrong_location", "other"].map((r) => (
                <button key={r} onClick={() => reportAbuse(r)}
                  className="w-full text-left px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 text-sm capitalize">
                  {r.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showStatus && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowStatus(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-gray-900">Change status</div>
            <select data-testid="status-select" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
              className="mt-3 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm">
              {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
            <textarea data-testid="status-note" value={statusNote} onChange={(e) => setStatusNote(e.target.value)}
              rows={3} placeholder="Optional update note (shared with followers)"
              className="mt-3 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShowStatus(false)} className="px-4 py-2 rounded-full border border-gray-200">Cancel</button>
              <button data-testid="status-save" onClick={submitStatus} disabled={busy} className="px-4 py-2 rounded-full bg-emerald-600 text-white font-semibold">
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showResolve && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowResolve(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-gray-900">Submit Resolution</div>
            <p className="text-xs text-gray-500 mt-1">GTF Admin will review before final approval.</p>
            <textarea data-testid="resolve-description" rows={3} value={resolveForm.description}
              onChange={(e) => setResolveForm({ ...resolveForm, description: e.target.value })}
              placeholder="Describe what was done (required)"
              className="mt-3 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <input value={resolveForm.work_completed} onChange={(e) => setResolveForm({ ...resolveForm, work_completed: e.target.value })}
              placeholder="Work completed (optional)" className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <input value={resolveForm.remaining_limitations} onChange={(e) => setResolveForm({ ...resolveForm, remaining_limitations: e.target.value })}
              placeholder="Remaining limitations (optional)" className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShowResolve(false)} className="px-4 py-2 rounded-full border border-gray-200">Cancel</button>
              <button data-testid="resolve-save" onClick={submitResolution} className="px-4 py-2 rounded-full bg-sky-600 text-white font-semibold">Submit</button>
            </div>
          </div>
        </div>
      )}

      {showUnable && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowUnable(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-gray-900">Unable to Resolve</div>
            <p className="text-xs text-gray-500 mt-1">Explain so GTF Admin can review and reassign or refer the issue.</p>
            <select data-testid="unable-reason" value={unableForm.reason} onChange={(e) => setUnableForm({ ...unableForm, reason: e.target.value })}
              className="mt-3 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm">
              {UNABLE_REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <textarea rows={2} value={unableForm.actions_taken} onChange={(e) => setUnableForm({ ...unableForm, actions_taken: e.target.value })}
              placeholder="Actions already taken" className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <textarea rows={2} value={unableForm.support_required} onChange={(e) => setUnableForm({ ...unableForm, support_required: e.target.value })}
              placeholder="Further support required" className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <input value={unableForm.department} onChange={(e) => setUnableForm({ ...unableForm, department: e.target.value })}
              placeholder="Relevant department (optional)" className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShowUnable(false)} className="px-4 py-2 rounded-full border border-gray-200">Cancel</button>
              <button data-testid="unable-save" onClick={submitUnable} className="px-4 py-2 rounded-full bg-rose-600 text-white font-semibold">Submit</button>
            </div>
          </div>
        </div>
      )}

      {showReferral && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowReferral(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-gray-900">Record External Referral</div>
            <input data-testid="referral-dept" value={referralForm.department} onChange={(e) => setReferralForm({ ...referralForm, department: e.target.value })}
              placeholder="Department / organization (required)" className="mt-3 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <input value={referralForm.reference_number} onChange={(e) => setReferralForm({ ...referralForm, reference_number: e.target.value })}
              placeholder="Reference number (optional)" className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <textarea rows={2} value={referralForm.note} onChange={(e) => setReferralForm({ ...referralForm, note: e.target.value })}
              placeholder="Note / follow-up details" className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShowReferral(false)} className="px-4 py-2 rounded-full border border-gray-200">Cancel</button>
              <button data-testid="referral-save" onClick={submitReferral} className="px-4 py-2 rounded-full bg-emerald-600 text-white font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
      {showEdit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowEdit(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-gray-900">Edit issue</div>
            <p className="text-xs text-gray-500 mt-1">A snapshot of the previous version is kept in the audit history.</p>
            <label className="mt-3 block text-xs font-semibold text-gray-700">Title</label>
            <input data-testid="edit-title" value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <label className="mt-3 block text-xs font-semibold text-gray-700">Description</label>
            <textarea data-testid="edit-description" rows={4} value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm whitespace-pre-line" />
            <label className="mt-3 block text-xs font-semibold text-gray-700">Village / Area</label>
            <input value={editForm.village}
              onChange={(e) => setEditForm({ ...editForm, village: e.target.value })}
              className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <label className="mt-3 block text-xs font-semibold text-gray-700">Landmark</label>
            <input value={editForm.landmark}
              onChange={(e) => setEditForm({ ...editForm, landmark: e.target.value })}
              className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShowEdit(false)} className="px-4 py-2 rounded-full border border-gray-200">Cancel</button>
              <button data-testid="edit-save" onClick={saveEdit} className="px-4 py-2 rounded-full bg-emerald-600 text-white font-semibold">Save changes</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
